// Вся арифметика проверки цен: насколько найденная цена отличается от цены
// блока, как давно проверяли и стоит ли ещё верить этой проверке.
//
// Чистые функции без React — компоненты только читают через них. Это же
// единственное место модуля, покрытое тестами: рендер-тестов в проекте нет.
import type { PriceCheck } from './types';

/**
 * Куда смотрит найденная цена относительно цены блока.
 * `none` — сравнивать не с чем: проверки не было, она не удалась или цена не
 * нашлась. Это не «столько же», и путать эти два случая нельзя.
 */
export type PriceDirection = 'cheaper' | 'dearer' | 'same' | 'none';

export interface PriceDelta {
  direction: PriceDirection;
  /** Знаковая разница «найдено − было», в минорных единицах валюты блока. */
  minor: number;
  /** Та же разница в процентах от базы, округлённая. 0, если база нулевая. */
  percent: number;
}

const NONE: PriceDelta = { direction: 'none', minor: 0, percent: 0 };

/**
 * Разница между найденной ценой и ценой блока.
 *
 * `currentMinor` — цена блока прямо сейчас. Её стоит передавать всегда, когда
 * валюта блока не менялась с момента проверки: `check.baseMinor` — это база на
 * момент проверки, и после того, как человек поставил себе найденную цену,
 * бейдж по этой базе вечно показывал бы «дешевле». Сравнение с сегодняшней
 * ценой честнее. Если валюты разошлись, база остаётся серверная: `bestMinor`
 * приведён к валюте блока на момент проверки, и мешать её с другой нельзя.
 */
export function priceDelta(
  check: PriceCheck | null | undefined,
  currentMinor?: number | null,
): PriceDelta {
  if (!check || check.status !== 'ok' || check.bestMinor === null) return NONE;

  const base = currentMinor ?? check.baseMinor;
  const minor = check.bestMinor - base;
  const direction: PriceDirection = minor === 0 ? 'same' : minor < 0 ? 'cheaper' : 'dearer';
  // Делить на ноль незачем: у блока с нулевой ценой процентов не бывает,
  // а само направление («стало дороже») остаётся верным.
  const percent = base === 0 ? 0 : Math.sign(minor) * Math.round((Math.abs(minor) / base) * 100);
  return { direction, minor, percent };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Проверка старше этого — уже не факт, а воспоминание: цены на билеты живут
 * днями, а не неделями. На эту границу завязан приглушённый вид бейджа, но не
 * его текст: скрывать старую проверку нельзя, «проверено {when}» и есть ответ.
 */
export const STALE_AFTER_DAYS = 3;

export type FreshnessBucket = 'today' | 'yesterday' | 'days' | 'old';

export interface Freshness {
  /** Целых календарных суток назад: 0 — сегодня, 1 — вчера. */
  days: number;
  bucket: FreshnessBucket;
  stale: boolean;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Календарные сутки, а не 24 часа: «вчера в 23:50» человек называет вчерашним
 * и в полночь, и в полдень. Округление, а не деление нацело, — из-за перехода
 * на летнее время сутки бывают в 23 и 25 часов.
 */
export function daysAgo(checkedAt: number, now: number): number {
  const diff = (startOfDay(now) - startOfDay(checkedAt)) / DAY_MS;
  return Math.max(0, Math.round(diff));
}

export function freshness(checkedAt: number, now: number): Freshness {
  const days = daysAgo(checkedAt, now);
  const bucket: FreshnessBucket =
    days === 0 ? 'today' : days === 1 ? 'yesterday' : days <= STALE_AFTER_DAYS ? 'days' : 'old';
  return { days, bucket, stale: days > STALE_AFTER_DAYS };
}

/** Верить проверке уже нельзя — но показывать её всё равно нужно. */
export function isStale(checkedAt: number, now: number): boolean {
  return freshness(checkedAt, now).stale;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Подпись «когда проверяли» для `prices.badge.checked` — штамп, а не слова.
 *
 * Слова («сегодня», «N дней назад») потребовали бы своих ключей во всех пяти
 * словарях и множественных форм на каждый язык, а штамп читается одинаково
 * везде и ничего не округляет: сегодня — время, в этом году — день и месяц,
 * раньше — с годом.
 */
export function formatWhen(checkedAt: number, now: number): string {
  const d = new Date(checkedAt);
  if (daysAgo(checkedAt, now) === 0) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  const date = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
  return sameYear ? date : `${date}.${d.getFullYear()}`;
}

/**
 * Полный штамп для списка истории: там строки стоят рядом, и короткое «14:22»
 * у одной против «03.08» у соседней не читается как одна шкала.
 */
export function formatStamp(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Сколько рейсов реально уйдёт на проверку. Предел сервера соблюдаем и на
 * клиенте: сервер обрежет лишнее молча, а человек должен узнать об этом до
 * запуска, а не гадать, почему часть карточек осталась без бейджа.
 */
export function limitFlights<T>(
  flights: readonly T[],
  maxFlights: number | null | undefined,
): { sent: T[]; trimmed: number } {
  const max = Number.isFinite(maxFlights) && (maxFlights as number) > 0 ? (maxFlights as number) : 0;
  if (max === 0 || flights.length <= max) return { sent: flights.slice(), trimmed: 0 };
  return { sent: flights.slice(0, max), trimmed: flights.length - max };
}

/**
 * Точки для спарклайна истории: значения слева направо от старых к новым,
 * растянутые по всей ширине и высоте.
 *
 * Шаг по горизонтали — равномерный по номеру проверки, а не по времени: между
 * проверками бывают недели, и честная временная ось превратила бы линию в
 * стену у правого края. Одна точка не линия — тогда возвращаем пустую строку,
 * и компонент просто не рисует <polyline>.
 */
export function sparklinePoints(
  values: readonly number[],
  width: number,
  height: number,
): string {
  if (values.length < 2 || width <= 0 || height <= 0) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const stepX = width / (values.length - 1);
  return values
    .map((value, i) => {
      const x = i * stepX;
      // Ровная линия при одинаковых ценах: делить на нулевой размах нельзя,
      // а «все точки посередине» — единственный неврущий вариант.
      const y = span === 0 ? height / 2 : height - ((value - min) / span) * height;
      return `${round2(x)},${round2(y)}`;
    })
    .join(' ');
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * История с сервера приходит новыми сверху. Для графика нужен обратный
 * порядок и только те строки, где есть что рисовать.
 */
export function historyValues(checks: readonly PriceCheck[]): number[] {
  return checks
    .filter((c) => c.bestMinor !== null)
    .map((c) => c.bestMinor as number)
    .reverse();
}
